import lang from 'lang'

import ErrorCodes from '@/enums/error-codes.enum'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { TextModuleService } from '@/modules/settings/components/text-modules-v2/textModuleService'
import { SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import { checkModuleAccess } from '@/modules'

// actions
const SHOW_LOADER = 'settings/textModules/SHOW_LOADER'
const HIDE_LOADER = 'settings/textModules/HIDE_LOADER'
const SHOW_DRAWER_LOADER = 'settings/textModules/SHOW_DRAWER_LOADER'
const HIDE_DRAWER_LOADER = 'settings/textModules/HIDE_DRAWER_LOADER'
const OPEN_DRAWER = 'settings/textModules/OPEN_DRAWER'
const CLOSE_DRAWER = 'settings/textModules/CLOSE_DRAWER'
const SET_TEXT_MODULES = 'settings/textModules/SET_TEXT_MODULES'
const SET_SELECTED_TEXT_MODULE = 'settings/textModules/SET_SELECTED_TEXT_MODULE'
const RESET_TEXT_MODULES = 'settings/textModules/RESET_TEXT_MODULES'

// reducer
const initialState = {
  isLoading: false,
  showDrawer: false,
  isDrawerLoading: false,
  textModules: [],
  selectedTextModule: undefined,
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SHOW_LOADER:
      return { ...state, isLoading: true }

    case HIDE_LOADER:
      return { ...state, isLoading: false }

    case SHOW_DRAWER_LOADER:
      return { ...state, isDrawerLoading: true }

    case HIDE_DRAWER_LOADER:
      return { ...state, isDrawerLoading: false }

    case OPEN_DRAWER: {
      const { selectedTextModule } = action
      return { ...state, showDrawer: true, selectedTextModule }
    }

    case CLOSE_DRAWER:
      return { ...state, showDrawer: false, selectedTextModule: undefined }

    case SET_SELECTED_TEXT_MODULE: {
      const { selectedTextModule } = action
      return { ...state, selectedTextModule }
    }

    case SET_TEXT_MODULES: {
      const { data } = action
      return { ...state, ...data }
    }

    case RESET_TEXT_MODULES: {
      return { ...state, selectedTextModule: undefined }
    }

    default:
      return state
  }
};

// action creators
const showLoader = () => {
  return {
    type: SHOW_LOADER,
  }
}

const hideLoader = () => {
  return {
    type: HIDE_LOADER,
  }
}

const showDrawerLoader = () => {
  return {
    type: SHOW_DRAWER_LOADER,
  }
}

const hideDrawerLoader = () => {
  return {
    type: HIDE_DRAWER_LOADER,
  }
}

export const openDrawer = (textModule) => ({
  type: OPEN_DRAWER,
  selectedTextModule: textModule,
})

export const closeDrawer = () => ({
  type: CLOSE_DRAWER,
})

export const resetTextModules = () => ({
  type: RESET_TEXT_MODULES,
})

const setTextModules = (data) => {
  return {
    type: SET_TEXT_MODULES,
    data,
  }
}

export const setSelectedModule = (textModule) => ({
  type: SET_SELECTED_TEXT_MODULE,
  selectedTextModule: textModule,
})

export const fetchTextModules = () => {
  return (dispatch) => {
    dispatch(showLoader())
    return TextModuleService.getAll()
      .then((textModules) => {
        const hasCollectiveInvoice = checkModuleAccess(SUBSCRIPTION_MODULES.COLLECTIVE_INVOICE)
        textModules = textModules.filter((textModule) => textModule.name !== 'Sammelrechnung' || hasCollectiveInvoice)
        dispatch(setTextModules({ textModules }))
        dispatch(hideLoader())
      })
      .catch(() => {
        dispatch(hideLoader())
      })
  }
}

export const createTextModule = (data) => {
  return (dispatch, getState) => {
    const { settings: { textModules: { textModules } } } = getState()
    dispatch(showDrawerLoader())

    return TextModuleService.create(data)
      .then(createdTextModule => {
        const { id } = createdTextModule
        const currentDefaultTextModule = textModules.find(tm => tm.isDefault && tm.type === data.type)
        const updatedTextModules = currentDefaultTextModule
          ? textModules.map(textModule => {
            // set isDefault false if other textModule is new default
            if (data.isDefault && currentDefaultTextModule.id !== id && textModule.id === currentDefaultTextModule.id) {
              return { ...textModule, isDefault: false }
            }
            return textModule
          })
          : textModules

        dispatch(setTextModules({ textModules: [...updatedTextModules, { ...data, id }] }))
        dispatch(setSelectedModule({ ...data, id }))
        toast.showToast({ message: lang.textModules.createSuccessMessage })
        return { ...data, id }
      })
      .catch((e) => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        throw e
      })
      .finally(() => dispatch(hideDrawerLoader()))
  }
}

export const updateTextModule = (id, data) => {
  return (dispatch, getState) => {
    dispatch(showDrawerLoader())
    const { settings: { textModules: { textModules } } } = getState()

    return TextModuleService.update(id, data)
      .then(() => {
        const currentDefaultTextModule = textModules.find(tm => tm.isDefault && tm.type === data.type)
        const updatedTextModules = textModules.map(textModule => {
          // set isDefault false if other textModule is new default
          if (data.isDefault && currentDefaultTextModule?.id !== id && textModule.id === currentDefaultTextModule?.id) {
            return { ...textModule, isDefault: false }
          }
          return textModule.id === id ? { ...textModule, ...data } : textModule
        })
        const updatedTextModule = updatedTextModules.find(textModule => textModule.id === id)

        dispatch(setTextModules({ textModules: updatedTextModules }))
        dispatch(setSelectedModule(updatedTextModule))
        toast.showToast({ message: lang.textModules.updateSuccessMessage })
        return updatedTextModule
      })
      .catch((e) => {
        let errorShown = false
        if (e?.body?.meta) {
          Object.keys(e?.body?.meta).forEach(key => {
            if (e?.body?.meta[key][0].code === ErrorCodes.IS_DEFAULT && !errorShown) {
              const message = lang.textModules.defaultTypeChangeError(lang.textModules.types[key])
              toast.showToast({ message, type: 'error' })
              errorShown = true
            }
          })
        }
        if (!errorShown) toast.showToast({ message: lang.defaultErrorMessage, type: 'error' })
        throw e
      })
      .finally(() => dispatch(hideDrawerLoader()))
  }
}

export const deleteTextModule = (id) => {
  return (dispatch, getState) => {
    TextModuleService.delete(id)
      .then(() => {
        const { settings: { textModules: { textModules } } } = getState()
        const textModuleToDelete = textModules.find(textModule => textModule.id === id)
        const filteredTextModules = textModules.filter(textModule => textModule.id !== id)
        if (textModuleToDelete.isDefault) {
          const systemTextModuleIndex = filteredTextModules.findIndex(tm => tm.type === textModuleToDelete.type && tm.isSystem)
          filteredTextModules[systemTextModuleIndex].isDefault = true
        }

        dispatch(setTextModules({ textModules: filteredTextModules }))
        toast.showToast({ message: lang.textModules.deleteSuccessMessage })
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.textModules.deleteErrorMessage })
      })
  }
}
